import React from "react";
import {
  BooleanField,
  Datagrid,
  DateField,
  Edit,
  Filter,
  List,
  SimpleForm,
  TextField,
  useRecordContext,
  useResourceContext,
  Labeled,
  FunctionField,
  SimpleShowLayout,
  ReferenceManyField,
  ReferenceField,
  RadioButtonGroupInput,
  SelectInput,
  TextInput,
  NullableBooleanInput,
  useUpdate,
} from "react-admin";
import { AlertCircle, Check, MessageSquarePlus, X } from "lucide-react";
import { IconButton, Tooltip } from "@mui/material";

const styles = {
  sm: { minWidth: 120 },
  lg: { minWidth: 140 },
  parent: { paddingLeft: 16, gap: 16, display: "flex" },
};

const aiModerationDecisionChoices = [
  { id: "approve", name: "Approve" },
  { id: "reject", name: "Reject" },
  { id: "needs_human_review", name: "Needs human review" },
  // The API's React-Admin filter parser maps this string to SQL NULL.
  { id: "null", name: "Unprocessed" },
];

export function formatAiModerationDecision(decision?: string | null) {
  if (!decision) return "Unprocessed";
  return (
    aiModerationDecisionChoices.find((choice) => choice.id === decision)
      ?.name || decision
  );
}

const filters = [
  // Default to the secretary's regular comment queue; blank includes all audiences.
  <SelectInput
    source="type"
    label="Type"
    choices={[
      { id: "comment", name: "Comment" },
      { id: "technical_issue", name: "Technical Issue" },
      { id: "ask_speaker", name: "Ask Speaker" },
    ]}
    emptyText=""
    alwaysOn
  />,
  <TextInput source="parent" alwaysOn />,
  <TextInput source="id" alwaysOn />,
  <TextInput label="Lecture ID" source="lecture_id" alwaysOn />,
  <TextInput label="Dose Number" source="dose_number" alwaysOn />,
  <TextInput label="User Name" source="author_name" alwaysOn />,
  <RadioButtonGroupInput
    alwaysOn
    label="Comment Target"
    source="comment_type"
    options={{
      row: true,
    }}
    choices={[
      { id: "all", name: "All" },
      { id: "lecture", name: "Lecture" },
      { id: "dose", name: "Dose" },
    ]}
  />,
  <NullableBooleanInput
    source="approved"
    alwaysOn
    format={(value) => {
      if (value === 1) return "true";
      if (value === 0) return "false";
      return null;
    }}
    parse={(value) => {
      if (value == "true") return 1;
      if (value == "false") return 0;
      return null;
    }}
  />,
  <SelectInput
    source="ai_moderation_decision"
    label="AI Decision"
    choices={aiModerationDecisionChoices}
    alwaysOn
  />,
];

const ExpandComment = () => {
  const record = useRecordContext();
  const resource = useResourceContext();

  const hasParent =
    record.parent && record.parent !== 0 && record.parent !== "0";
  return (
    <Edit
      resource={resource}
      id={record.id}
      /* disable the app title change when shown */
      title=" "
      mutationMode="optimistic"
    >
      <SimpleForm redirect={false}>
        <Labeled>
          <TextField source="content" multiline label="Comment" />
        </Labeled>
        <Labeled label="AI Decision">
          <FunctionField
            render={(comment) =>
              comment.type === "comment"
                ? formatAiModerationDecision(comment.ai_moderation_decision)
                : "-"
            }
          />
        </Labeled>
        {/* A nullable control keeps unreviewed comments distinct from human rejections. */}
        {record.type === "comment" && (
          <NullableBooleanInput
            source="approved"
            label="Approved"
            nullLabel="Unreviewed"
            falseLabel="Rejected"
            trueLabel="Approved"
          />
        )}

        {hasParent ? (
          <Labeled>
            <ReferenceField
              source="parent"
              reference="manage-user-comments"
              label="Parent Comment"
            >
              <div style={styles.parent}>
                <Labeled>
                  <TextField source="id" />
                </Labeled>
                <Labeled>
                  <TextField source="content" />
                </Labeled>
                <Labeled>
                  <TextField
                    source="author_name"
                    label="User"
                    style={styles.sm}
                  />
                </Labeled>
                <Labeled>
                  <DateField
                    source="created_at"
                    label="Created"
                    style={styles.lg}
                  />
                </Labeled>
                <Labeled>
                  <BooleanField source="approved" label="Approved" />
                </Labeled>
              </div>
            </ReferenceField>
          </Labeled>
        ) : (
          <></>
        )}
        {/* This doesn't work on the api yet:  {record.child_count > 0 ? (
          <></>
        ) : (
          <Typography
            component="span"
            variant="body1"
            // style={styles.inlineFieldRight}
          >
            No replies
          </Typography>
        )}
        {record.lecture_id ? (
          <></>
        ) : (
          <BooleanInput source="featured" label="Featured" />
        )} */}
      </SimpleForm>
    </Edit>
  );
};

const HumanApprovalField = () => {
  const record = useRecordContext();
  const [update, { isLoading }] = useUpdate();

  if (!record) return null;
  // Private conversations have no publication decision, regardless of stored approval.
  if (record.type !== "comment") return <>-</>;

  const isUnreviewed =
    record.approved === null ||
    record.approved === undefined ||
    record.approved === "";

  if (!isUnreviewed) {
    return <BooleanField source="approved" />;
  }

  const applyDecision = (approved) => {
    update(
      "manage-user-comments",
      {
        id: record.id,
        data: { approved },
        previousData: record,
      },
      {
        // Update the row immediately while retaining the expanded editor for corrections.
        mutationMode: "optimistic",
      },
    );
  };

  return (
    <span style={{ display: "inline-flex" }}>
      <Tooltip title="Approve">
        <IconButton
          aria-label="Approve comment"
          color="success"
          size="small"
          disabled={isLoading}
          onClick={(event) => {
            event.stopPropagation();
            applyDecision(true);
          }}
        >
          <Check size={18} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Reject">
        <IconButton
          aria-label="Reject comment"
          color="error"
          size="small"
          disabled={isLoading}
          onClick={(event) => {
            event.stopPropagation();
            applyDecision(false);
          }}
        >
          <X size={18} />
        </IconButton>
      </Tooltip>
    </span>
  );
};

function ListView(props) {
  return (
    <List
      {...props}
      exporter={true}
      filters={filters}
      filterDefaultValues={{ type: "comment" }}
      perPage={50}
      sort={{ field: "created_at", order: "DESC" }}
      title="Comments"
    >
      <Datagrid
        rowClick="expand"
        bulkActionButtons={false}
        expand={<ExpandComment />}
        expandSingle
      >
        <TextField source="author_name" label="User" style={styles.sm} />
        <DateField source="created_at" label="Created" style={styles.lg} />
        <TextField source="user_email" label="Email" style={styles.sm} />
        <HumanApprovalField label="Approved" />
        <FunctionField
          label="AI Decision"
          render={(record) => {
            // Match the Approved column's status icons while leaving unprocessed comments visually empty.
            switch (record.ai_moderation_decision) {
              case "approve":
                return <Check />;
              case "reject":
                return <X />;
              case "needs_human_review":
                return <AlertCircle />;
              default:
                return null;
            }
          }}
        />
        <FunctionField
          label="Content"
          render={(record: { content: string }) => {
            const comment = record.content.slice(0, 50);
            if (comment.length < record.content.length) return comment + "...";
            return comment;
          }}
        />
        <FunctionField
          label="Lecture"
          render={(record) => {
            // Combine the comment's target and featured status to keep related metadata in one compact column.
            const isDose = Boolean(record.dose_id);
            const lectureIdOrDoseNumber = isDose
              ? record.daily_dose?.dose_number
              : record.lecture_id;
            const isFeatured = record.featured;

            return `${isDose ? "Dose " : ""}${lectureIdOrDoseNumber}${
              isFeatured ? " (Featured)" : ""
            }`;
          }}
        />
      </Datagrid>
    </List>
  );
}

export const UserComments = {
  category: "manage",
  name: "manage-user-comments",
  icon: MessageSquarePlus,
  list: ListView,
  options: { label: "Comments" },
};
